export const PRODUCT_IMAGE_TONE = {
	Pink: 0,
	Yellow: 1,
	Tosca: 2,
	SoftPink: 3,
} as const;

export type ProductImageTone =
	(typeof PRODUCT_IMAGE_TONE)[keyof typeof PRODUCT_IMAGE_TONE];

export type CatalogStoreLine = {
	sellingPrice: number;
	commissionPercent: number;
	stockQuantity: number;
	soldPerThirtyDays: number;
};

export type CatalogProduct = {
	productId: string;
	productCode: string;
	productName: string;
	imageTone: ProductImageTone;
	factoryBarcode?: string;
	isInactive?: boolean;
	isNeverStocked?: boolean;
	isPermitRequired?: boolean;
	productTypeName: string;
	categoryName: string;
	subCategoryName: string;
	netContent: string;
	storageMethod: string;
	shelfLifeDays: number;
	permitTypeName: string;
	permitNumber: string;
	productDescription: string;
	registeredDate: string;
	registrationNumber: string;
	photoCount: number;
	isLegacy?: boolean;
	storeLines: CatalogStoreLine[];
};

const STORE_COMMISSION_PERCENT = [30, 32, 30];

const toStoreLines = (
	sellingPrices: number[],
	stockQuantities: number[],
	soldPerThirtyDays: number[],
): CatalogStoreLine[] =>
	sellingPrices.map((sellingPrice, storeIndex) => ({
		sellingPrice,
		commissionPercent: STORE_COMMISSION_PERCENT[storeIndex] ?? 30,
		stockQuantity: stockQuantities[storeIndex] ?? 0,
		soldPerThirtyDays: soldPerThirtyDays[storeIndex] ?? 0,
	}));

// Lines follow store order.
export const ACTIVE_PARTNER_CATALOG: CatalogProduct[] = [
	{
		productId: "019a3f1c-7e20-7a41-9c01-5b8e4d21a001",
		productCode: "899743540208",
		productName: "Keripik Singkong Balado 150 g",
		imageTone: PRODUCT_IMAGE_TONE.Pink,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Keripik",
		netContent: "150 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 180,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010123-26",
		productDescription:
			"Keripik singkong iris tipis dengan bumbu balado pedas manis. Tanpa pengawet.",
		registeredDate: "2026-08-04",
		registrationNumber: "REP/260804/518204",
		photoCount: 3,
		storeLines: toStoreLines([25000, 25000, 25000], [12, 8, 0], [30, 22, 15]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c02-5b8e4d21a002",
		productCode: "574101949307",
		productName: "Keripik Pisang Cokelat 120 g",
		imageTone: PRODUCT_IMAGE_TONE.Yellow,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Keripik",
		netContent: "120 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 120,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010188-27",
		productDescription: "Keripik pisang kepok dilapisi cokelat susu.",
		registeredDate: "2026-08-04",
		registrationNumber: "REP/260804/731905",
		photoCount: 2,
		storeLines: toStoreLines([22000, 22000, 22000], [6, 10, 9], [18, 25, 12]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c03-5b8e4d21a003",
		productCode: "699170286139",
		productName: "Sambal Bawang Botol 150 ml",
		imageTone: PRODUCT_IMAGE_TONE.Tosca,
		factoryBarcode: "8997654321013",
		productTypeName: "Food",
		categoryName: "Bumbu & Saus",
		subCategoryName: "Sambal",
		netContent: "150 ml",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 180,
		permitTypeName: "BPOM",
		permitNumber: "MD 224510012345",
		productDescription: "Sambal bawang dalam botol kaca, pedas gurih.",
		registeredDate: "2026-08-12",
		registrationNumber: "REP/260812/402117",
		photoCount: 4,
		storeLines: toStoreLines([30000, 30000, 30000], [2, 4, 3], [9, 11, 7]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c04-5b8e4d21a004",
		productCode: "623352604864",
		productName: "Keripik Kentang Original 100 g",
		imageTone: PRODUCT_IMAGE_TONE.SoftPink,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Keripik",
		netContent: "100 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 180,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010201-26",
		productDescription: "Keripik kentang iris tipis, rasa original.",
		registeredDate: "2026-08-20",
		registrationNumber: "REP/260820/115683",
		photoCount: 2,
		storeLines: toStoreLines([18000, 18000, 18000], [15, 12, 20], [14, 10, 16]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c05-5b8e4d21a005",
		productCode: "281172683728",
		productName: "Kue Bawang Gurih 200 g",
		imageTone: PRODUCT_IMAGE_TONE.Pink,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Kue Kering",
		netContent: "200 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 120,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010245-27",
		productDescription: "Kue bawang renyah gurih dalam toples plastik.",
		registeredDate: "2026-08-20",
		registrationNumber: "REP/260820/680342",
		photoCount: 2,
		storeLines: toStoreLines([28000, 28000, 28000], [0, 0, 0], [12, 8, 6]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c06-5b8e4d21a006",
		productCode: "356198238414",
		productName: "Emping Melinjo Pedas 150 g",
		imageTone: PRODUCT_IMAGE_TONE.Yellow,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Keripik",
		netContent: "150 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 180,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010260-26",
		productDescription: "Emping melinjo dengan bumbu pedas.",
		registeredDate: "2026-08-28",
		registrationNumber: "REP/260828/237516",
		photoCount: 1,
		storeLines: toStoreLines([35000, 35000, 35000], [7, 5, 6], [6, 4, 5]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c07-5b8e4d21a007",
		productCode: "355026730862",
		productName: "Rengginang Wijen 250 g",
		imageTone: PRODUCT_IMAGE_TONE.Tosca,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Kerupuk",
		netContent: "250 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 90,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010277-28",
		productDescription: "Rengginang ketan dengan taburan wijen, siap goreng.",
		registeredDate: "2026-09-02",
		registrationNumber: "REP/260902/990318",
		photoCount: 2,
		storeLines: toStoreLines([27000, 27000, 27000], [9, 3, 11], [8, 6, 9]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c08-5b8e4d21a008",
		productCode: "165782751746",
		productName: "Sambal Kemiri Botol 100 ml",
		imageTone: PRODUCT_IMAGE_TONE.SoftPink,
		productTypeName: "Food",
		categoryName: "Bumbu & Saus",
		subCategoryName: "Sambal",
		netContent: "100 ml",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 120,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010281-26",
		productDescription: "Sambal kemiri khas rumahan dalam botol kaca.",
		registeredDate: "2026-09-02",
		registrationNumber: "REP/260902/446720",
		photoCount: 3,
		storeLines: toStoreLines([27000, 27000, 27000], [4, 6, 1], [7, 9, 5]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c09-5b8e4d21a009",
		productCode: "283378861381",
		productName: "Dodol Cokelat Kotak 200 g",
		imageTone: PRODUCT_IMAGE_TONE.Pink,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Dodol",
		netContent: "200 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 60,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010123-26",
		productDescription:
			"Dodol ketan rasa cokelat, dibungkus per potong dalam kotak.",
		registeredDate: "2026-09-10",
		registrationNumber: "REP/260910/308451",
		photoCount: 3,
		storeLines: toStoreLines([32000, 34000, 32000], [5, 5, 5], [3, 4, 2]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c10-5b8e4d21a010",
		productCode: "134759234543",
		productName: "Kacang Bawang Renyah 200 g",
		imageTone: PRODUCT_IMAGE_TONE.Yellow,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Nuts",
		netContent: "200 g",
		storageMethod: "",
		shelfLifeDays: 0,
		permitTypeName: "",
		permitNumber: "",
		productDescription: "",
		registeredDate: "2025-11-03",
		registrationNumber: "",
		photoCount: 0,
		isLegacy: true,
		storeLines: toStoreLines([20000, 20000, 20000], [10, 14, 8], [21, 19, 17]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c11-5b8e4d21a011",
		productCode: "151462779163",
		productName: "Keripik Tempe Original 100 g",
		imageTone: PRODUCT_IMAGE_TONE.Tosca,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Keripik",
		netContent: "100 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 90,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010302-26",
		productDescription: "Keripik tempe tipis, rasa original.",
		registeredDate: "2026-09-18",
		registrationNumber: "REP/260918/762039",
		photoCount: 2,
		storeLines: toStoreLines([15000, 15000, 15000], [0, 3, 0], [16, 12, 10]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c12-5b8e4d21a012",
		productCode: "914199618985",
		productName: "Paket Hampers Lebaran",
		imageTone: PRODUCT_IMAGE_TONE.SoftPink,
		isInactive: true,
		productTypeName: "Food",
		categoryName: "Parsel",
		subCategoryName: "Hampers",
		netContent: "1 paket",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 90,
		permitTypeName: "",
		permitNumber: "",
		productDescription: "Paket hampers Lebaran berisi 5 produk kering pilihan.",
		registeredDate: "2026-03-01",
		registrationNumber: "REP/260301/584770",
		photoCount: 2,
		storeLines: toStoreLines([150000, 150000, 150000], [0, 0, 0], [0, 0, 0]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c13-5b8e4d21a013",
		productCode: "470915382614",
		productName: "Abon Sapi Pedas 100 g",
		imageTone: PRODUCT_IMAGE_TONE.Yellow,
		isNeverStocked: true,
		isPermitRequired: true,
		productTypeName: "Food",
		categoryName: "Lauk Siap Saji",
		subCategoryName: "Abon",
		netContent: "100 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 180,
		permitTypeName: "",
		permitNumber: "",
		productDescription: "Abon sapi pedas, tanpa pengawet tambahan.",
		registeredDate: "2026-10-09",
		registrationNumber: "REP/261009/207993",
		photoCount: 3,
		storeLines: toStoreLines([45000, 45000, 45000], [0, 0, 0], [0, 0, 0]),
	},
];

export const NEW_PARTNER_CATALOG: CatalogProduct[] = [
	{
		productId: "019a3f1c-7e20-7a41-9c14-5b8e4d21a014",
		productCode: "612345670011",
		productName: "Keripik Tempe 100 g",
		imageTone: PRODUCT_IMAGE_TONE.SoftPink,
		isNeverStocked: true,
		productTypeName: "Food",
		categoryName: "Snack",
		subCategoryName: "Keripik",
		netContent: "100 g",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 180,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010999-26",
		productDescription: "Keripik tempe tipis, rasa original.",
		registeredDate: "2026-10-08",
		registrationNumber: "REP/261008/118320",
		photoCount: 2,
		storeLines: toStoreLines([20000, 20000], [0, 0], [0, 0]),
	},
	{
		productId: "019a3f1c-7e20-7a41-9c15-5b8e4d21a015",
		productCode: "612345670028",
		productName: "Es Teh Lemon 250 ml",
		imageTone: PRODUCT_IMAGE_TONE.Yellow,
		isNeverStocked: true,
		productTypeName: "Beverages",
		categoryName: "Minuman Ringan",
		subCategoryName: "Teh",
		netContent: "250 ml",
		storageMethod: "Suhu ruang",
		shelfLifeDays: 120,
		permitTypeName: "P-IRT",
		permitNumber: "2063201010998-26",
		productDescription: "Es teh lemon siap minum.",
		registeredDate: "2026-10-08",
		registrationNumber: "REP/261008/118321",
		photoCount: 2,
		storeLines: toStoreLines([12000, 12000], [0, 0], [0, 0]),
	},
];
